import React from 'react'
import {toPairs} from 'lodash'
import hotToast from 'react-hot-toast'
import {useForm as useRHF} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import * as zod from 'zod'
import {create} from 'zustand'
import {produce} from 'immer'
import {SingleValue} from 'react-select'
import {Team, User, Scope} from './model'
import {Box, Paper, Typography} from '@mui/material'
import {NetworkError} from '@data-client/rest'
import {LinearProgress} from '@mui/material'
import {useLocation} from 'wouter'
import {LngLatBoundsLike} from 'mapbox-gl'
import {IMapable} from '@fotolog/data'
import {micromark} from 'micromark'

zod.setErrorMap((error, ctx) => {
  // console.log('ZOD!', {error, ctx})
  switch (error.code) {
    case zod.ZodIssueCode.invalid_type:
      if (error.expected === 'date' && error.received === 'null') {
        return {message: 'required'}
      }
      break
    case zod.ZodIssueCode.too_small:
      if (error.type === 'string' && error.minimum === 1) {
        return {message: 'required'}
      }
      break
  }
  return {message: ctx.defaultError}
})

export function getDataAttr(name: string, el: HTMLElement) {
  const attr = `data-${name}`
  let val
  while (el.parentElement) {
    val = el.parentElement.getAttribute(attr)
    if (val) {
      return val
    }
    el = el.parentElement
  }
  return null
}

export function downloadName(kind: string, name: string, ext: string) {
  return `${kind} - ${name.replace(/\W+/gu, ' ')}.${ext}`
}

export const FormTransforms = {
  date: (val: Date | '' | null) => {
    return val ? val.toISOString().slice(0, 10) : null
  },
  http: (val: string | null) => {
    if (!val || val.match(/https?:/iu)) {
      return val
    }
    return `http://${val}`
  },
  // emptyAsNull: (val: string, orig: string) => {
  //   return String(orig).trim() === '' ? null : val
  // },
}

// convert persisted values to Select options
export function val2opt(value: unknown) {
  // TODO: wean off unknown
  return {value, label: value} as SelectOption
}

export type SelectOption = {
  value: unknown
  label: string | JSX.Element
}

export type UserSelectOption = SingleValue<{value: User; label: Element}>

export interface SessionState {
  fetched: boolean
  isAdmin: boolean
  isManaging: () => boolean
  id: number
  user: User
  scope: Scope
  update: SessionStateSetter
  euid: () => number
  getSettings: () => Team
  signIn: (qryscope?: number) => void
  keys: {
    mapbox: string
  }
}
type SessionStateSetter = (x: Partial<SessionState> | ((x: SessionState) => SessionState)) => SessionState

const sessionStore = (set: SessionStateSetter, get: () => SessionState) => ({
  fetched: false,
  isAdmin: false,
  isManaging: () => {
    const scopedUserId = get().scope?.id
    const sessionUserId = get().user?.id
    return scopedUserId && scopedUserId !== sessionUserId
  },
  id: 0,
  user: null,
  scope: null,
  update: (f: SessionStateSetter) => set(produce(f)),
  euid: () => get().scope?.id || 0,
  getSettings: () => get().scope?.team || {},
  signIn: async (qryscope?: number) => {
    try {
      const scope = JSON.parse(localStorage.getItem('session.scope') || '{}')
      const scopeId = qryscope || scope?.id || ''
      const r = await appApi('GET', `/session?scope=${scopeId}`)
      const data = await r.json()
      if (data.scope) {
        localStorage.setItem('session.scope', JSON.stringify(data.scope))
      }
      data.fetched = true
      set(data)
    } catch (err) {
      set({fetched: true})
      console.error('FAILED AUTH:', err)
    }
  },
})
export const useSession = create<SessionState>(produce(sessionStore))

export interface FormFieldProps {
  name: string
  type:
    | 'text'
    | 'textarea'
    | 'password'
    | 'showable-password'
    | 'image'
    | 'select'
    | 'switch'
    | 'address'
    | 'date'
    | 'color'
    | 'rating'
  // type: string
  label: string
  required?: boolean
  placeholder?: string
  help?: JSX.Element | string
  schema?: zod.AnyZodObject
  style?: Record<string, string>
  // textarea attrs
  rows?: number
  maxChars?: number
  // image attrs
  width?: number
  height?: number
  // select attrs
  options?: SelectOption[]
  searchable?: boolean
  multi?: boolean
  creatable?: boolean
}

// TODO: maybe this will help https://github.com/colinhacks/zod#writing-generic-functions
type UseFormOptions = {
  // TODO: Record<string, unknown> breaks down with nested objects (eg theme from session.getSettings())
  defaults?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
  mode?: 'onSubmit' | 'onChange'
}
export function useForm(schema: zod.AnyZodObject, {defaults = {}, mode = 'onSubmit'}: UseFormOptions = {}) {
  type Schema = zod.infer<typeof schema>
  const form = useRHF<Schema>({
    mode,
    // reValidateMode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: defaults,
  })

  return form
}

export type ToastLevel = 'loading' | 'info' | 'error' | 'custom'

export function toast(level: ToastLevel, msg: string) {
  let method = hotToast.loading
  let duration = Infinity
  if (level === 'info') {
    method = hotToast.success
    duration = 5000
  } else if (level === 'custom') {
    method = hotToast.custom
    duration = 10000
  } else if (level === 'error') {
    method = hotToast.error
    duration = 30000
  }
  const opts = {
    duration,
    className: 'app-toast',
  }
  const id = method(<div>{msg}</div>, opts)
  return {
    progress: (percent: number) => {
      hotToast.loading(
        <div>
          {msg} <LinearProgress variant="determinate" value={percent} />
        </div>,
        {id}
      )
    },
    remove: () => hotToast.dismiss(id),
    succeed: (txt?: string) => {
      hotToast.success(
        <div>
          <div>{msg}</div>
          <div>↳ {txt || 'Success!'}</div>
        </div>,
        {...opts, id, duration: 5000}
      )
    },
    fail: (txt?: string) => {
      hotToast.error(
        <div>
          <div>{msg}</div>
          <div>↳ {txt || 'Failed'}</div>
        </div>,
        {...opts, id, duration: 30000}
      )
    },
  }
}

export function checkForUpgrade() {
  if (window.app.upgrade) {
    toast('loading', 'Upgrading Application')
    setTimeout(() => window.location.reload(), 5 * 1000)
  }
}

export function useAppNav() {
  const [location, setLocation] = useLocation()
  return {
    to: (url: string, {replace = false}: {replace?: boolean} = {}) => {
      if (window.app.upgrade) {
        toast('loading', 'Upgrading Application')
        setTimeout(() => {
          window.location.assign(url)
        }, 3 * 1000)
      } else {
        if (!url.startsWith('/')) {
          url = `${location}/${url}`
        }
        setLocation(url, {replace})
      }
    },
    back: () => {
      if (window.app.upgrade) {
        toast('loading', 'Upgrading Application')
        setTimeout(() => window.location.reload(), 3 * 1000)
      }
      window.history.go(-1)
      // guard against duplicate entries in the browser history
      // let attempts = 0
      // const goBack = () => {
      //   attempts++
      //   if (attempts < 10 && lastLocation === window.location.pathname) {
      //     // using setTimeout to give browser a chance to a go back
      //     window.history.go(-1)
      //     setTimeout(goBack, 200)
      //   }
      // }
      // goBack()
    },
  }
}

export function useAppNav1() {
  const [lastLocation, setLocation] = useLocation()
  return (to: string) => {
    if (to === 'BACK') {
      if (window.app.upgrade) {
        toast('loading', 'Upgrading Application')
        setTimeout(() => window.location.reload(), 5 * 1000)
      } else {
        // guarding against duplicate entries in the browser history
        let attempts = 0
        // const lastLocation = window.location.pathname
        const goBack = () => {
          attempts++
          if (attempts < 10 && lastLocation === window.location.pathname) {
            // using setTimeout to give browser a chance to a go back
            window.history.go(-1)
            setTimeout(goBack, 200)
          }
        }
        goBack()
      }
    } else if (window.app.upgrade) {
      toast('loading', 'Upgrading Application')
      setTimeout(() => {
        window.location.assign(to)
      }, 5 * 1000)
    } else {
      setLocation(to)
      // window.history.pushState({}, '', to)
    }
  }
}

type AppApiReply = {status?: number; error?: string} | Promise<any> // eslint-disable-line @typescript-eslint/no-explicit-any

export async function apiReply(f: () => AppApiReply) {
  // rest-hooks will throw if response is not successful (400, 500), but we still want the response body
  try {
    return await f()
  } catch (exc) {
    const err = exc as NetworkError
    const status = err.response?.status
    if (status >= 400 && status < 500) {
      try {
        return await err.response.json()
      } catch {
        // handle client errors (eg 401 Unauthorized)
        return {status, error: err.response?.statusText}
      }
    }
    // let it fail - our error handler will do the needful
    throw exc
  }
}

export function useDiagnosticReport() {
  const show = useToggle()

  function asText(s: unknown) {
    if (typeof s === 'object') {
      return JSON.stringify(s)
    }
    return s
  }

  function copyToClipboard(evt: React.MouseEvent) {
    const tgt = evt.currentTarget as HTMLElement
    navigator.clipboard.writeText(tgt.innerText)
    toast('info', 'Copied to Clipboard')
  }

  function localStorageToReport() {
    return Object.keys(localStorage)
      .sort()
      .filter(i => i.match(/(version|session.scope)/u))
  }

  function browserItemsToReport() {
    return {
      vendor: navigator.vendor,
      platform: navigator.platform,
      version: navigator.appVersion,
    }
  }
  const text = `
Browser Details
_______________

- ${toPairs(browserItemsToReport())
    .sort()
    .map(([name, val]) => `${name}: ${val}`)
    .join('\n- ')}


Local Storage
_____________

- ${localStorageToReport()
    .map(key => `${key}: ${asText(localStorage[key])}`)
    .join('\n- ')}


Error Messages
______________

${window.app.errors.map(e => `${e.ts}\n${e.msg}`).join('\n\n')}
`

  function resetSession() {
    window.location.assign('/signout')
  }

  function Report() {
    if (!show.enabled) {
      return null
    }
    return (
      <Box my={3}>
        <Typography variant="h3">
          Diagnostic Report <small>(click to copy)</small>
        </Typography>
        <Paper
          className="app-copyable"
          sx={{p: 2, width: '100%', whiteSpace: 'pre-line', overflow: 'hidden'}}
          onClick={copyToClipboard}
        >
          {text}
        </Paper>
      </Box>
    )
  }

  return {Report, resetSession, toggle: show.toggle}
}

export interface useToggleReturn {
  enabled: boolean
  toggle: () => void
  on: () => void
  off: () => void
}

// Better DX for simple boolean state variable
//
export function useToggle(init = false) {
  const [enabled, set] = React.useState(init)
  return {
    enabled,
    toggle: () => set(!enabled),
    on: () => set(true),
    off: () => set(false),
  } as useToggleReturn
}

export function appApi(method: 'GET' | 'POST', path: string, data = {}) {
  const url = `/api${path}`
  const headers = new Headers()
  // const opts = {
  //   method,
  //   credentials: 'include',
  //   // credentials: 'same-origin',
  // } as RequestInit
  // if (token) {
  //   opts.headers.Authorization = `Bearer ${token}`
  // }
  let body
  if (method !== 'GET' && data) {
    if (data instanceof FormData) {
      body = data
    } else if (typeof data === 'object') {
      headers.set('Content-Type', 'application/json')
      body = JSON.stringify(data)
    } else {
      body = data as BodyInit
    }
  }
  return fetch(url, {
    method,
    body,
    headers,
    credentials: 'include',
  })
}

export function rgb2hsl(rgb: string | [number, number, number]) {
  let r, g, b
  if (typeof rgb === 'string') {
    r = Number('0x' + rgb.slice(1, 3)) / 255
    g = Number('0x' + rgb.slice(3, 5)) / 255
    b = Number('0x' + rgb.slice(5, 7)) / 255
  } else {
    r = rgb[0]
    g = rgb[1]
    b = rgb[2]
  }

  const cmin = Math.min(r, g, b)
  const cmax = Math.max(r, g, b)
  const delta = cmax - cmin
  let h = 0
  let s = 0
  let l = 0

  // hue
  if (delta === 0) {
    h = 0
  } else if (cmax === r) {
    h = ((g - b) / delta) % 6
  } else if (cmax === g) {
    h = (b - r) / delta + 2
  } else {
    h = (r - g) / delta + 4
  }
  h = Math.round(h * 60)
  if (h < 0) {
    h += 360
  }

  l = (cmax + cmin) / 2

  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

  s = Math.round(s * 100)
  l = Math.round(l * 100)

  return [h, s, l]
}

export function applyTheme({primary}: {primary: string}) {
  const [h, s, l] = rgb2hsl(primary)
  document.documentElement.style.setProperty('--primary-hue', `${h}`)
  document.documentElement.style.setProperty('--primary-saturation', `${s}%`)
  document.documentElement.style.setProperty('--primary-lightness', `${l}%`)
  document.documentElement.style.setProperty('--primary-hsl', `${h}, ${s}%, ${l}%`)
}

export function scrollTo(id: string, behavior: 'auto' | 'smooth' = 'auto') {
  const el = document.getElementById(id)
  if (el) {
    el.scrollIntoView({behavior})
  } else {
    console.error('element does not exist:', id)
  }
}

export function tripBounds(points: IMapable[]) {
  const bl = {lat: 0, lng: 0}
  const tr = {...bl}
  for (const pt of points) {
    if (!bl.lat) {
      bl.lat = pt.gps.lat
      tr.lat = pt.gps.lat
    } else if (pt.gps.lat < bl.lat) {
      bl.lat = pt.gps.lat
    } else if (pt.gps.lat > tr.lat) {
      tr.lat = pt.gps.lat
    }
    if (!bl.lng) {
      bl.lng = pt.gps.lng
      tr.lng = pt.gps.lng
    } else if (pt.gps.lng < bl.lng) {
      bl.lng = pt.gps.lng
    } else if (pt.gps.lng > tr.lng) {
      tr.lng = pt.gps.lng
    }
  }
  // zoom out a bit so markers aren't on the edge
  bl.lat += 0.2 * (bl.lat - tr.lat)
  tr.lat -= 0.2 * (bl.lat - tr.lat)
  bl.lng += 0.2 * (bl.lng - tr.lng)
  tr.lng -= 0.2 * (bl.lng - tr.lng)
  return [bl, tr] as LngLatBoundsLike
}

export function galleryCaption(title: string, text: string) {
  if (!title) {
    return ''
  }
  if (!text) {
    return `<div class="summary">${title}</div>`
  }
  return `<details open><summary>${title}</summary><section>${micromark(text)}</section></details>`
}
